
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Clock, Target, Info, CheckCircle2 } from "lucide-react";

interface AssessmentStartProps {
  onStart: () => void;
}

export const AssessmentStart = ({ onStart }: AssessmentStartProps) => {
  console.log("Rendering AssessmentStart component");
  
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Main intro card */}
      <Card className="bg-gradient-card border-0 shadow-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-4 text-text-primary">
            Product Leadership Assessment
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            A comprehensive evaluation of your product management capabilities across 6 core dimensions
          </p>
        </div>

        {/* How it works */}
        <div className="bg-background/50 p-6 rounded-lg border border-border/50 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Info className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-lg">How This Assessment Works</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-primary">1</span>
              </div>
              <h4 className="font-medium mb-2">Answer Questions</h4>
              <p className="text-sm text-text-secondary">
                Read each question carefully and click the response that best describes your experience and capabilities
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-primary">2</span>
              </div>
              <h4 className="font-medium mb-2">Get Your Score</h4>
              <p className="text-sm text-text-secondary">
                Receive a comprehensive score (0-120) that reflects your product leadership level
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-primary">3</span>
              </div>
              <h4 className="font-medium mb-2">Get Guidance</h4>
              <p className="text-sm text-text-secondary">
                Receive personalized recommendations and identify your strongest areas and growth opportunities
              </p>
            </div>
          </div>

          <div className="bg-accent-blue/10 p-4 rounded-lg border-l-4 border-accent-blue">
            <h4 className="font-medium text-accent-blue mb-2">💡 How to Choose Your Answers</h4>
            <p className="text-sm text-text-secondary">
              Select the option that <strong>best describes your consistent behavior and impact</strong>. 
              Choose based on what you can demonstrate with specific examples, not aspirations. 
              When in doubt, pick the more conservative option.
            </p>
          </div>
        </div>

        {/* Assessment details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
            <Clock className="w-5 h-5 text-accent-green" />
            <div>
              <div className="font-medium">Time Required</div>
              <div className="text-sm text-text-secondary">10-15 minutes</div>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
            <Target className="w-5 h-5 text-accent-orange" />
            <div>
              <div className="font-medium">Question Format</div>
              <div className="text-sm text-text-secondary">Multiple choice with examples</div>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
            <CheckCircle2 className="w-5 h-5 text-accent-pink" />
            <div>
              <div className="font-medium">6 Dimensions</div>
              <div className="text-sm text-text-secondary">Comprehensive skill evaluation</div>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
            <Badge variant="secondary" className="w-5 h-5 flex items-center justify-center p-0">
              <span className="text-xs">120</span>
            </Badge>
            <div>
              <div className="font-medium">Max Score</div>
              <div className="text-sm text-text-secondary">Industry-leading capabilities</div>
            </div>
          </div>
        </div>

        {/* Assessment sections preview */}
        <div className="mb-8">
          <h3 className="font-semibold text-lg mb-4">What You'll Be Evaluated On:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Product Craft & Technical Excellence',
              'Customer Discovery & Understanding', 
              'Research & Design Mastery',
              'Market & Business Impact',
              'Product Operations & Team Leadership',
              'Industry Impact & Influence'
            ].map((dimension, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span>{dimension}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center">
          <Button 
            onClick={onStart}
            size="lg"
            className="bg-primary hover:bg-primary-hover text-white px-8 py-4 rounded-lg 
                     transition-all duration-200 shadow-md hover:shadow-lg 
                     hover:scale-[1.02] border-0 inline-flex items-center text-lg"
          >
            <span>Start Assessment</span>
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-xs text-text-muted mt-3">
            Your responses are private and used only for generating your personalized results
          </p>
        </div>
      </Card>
    </div>
  );
};
